/*
<summary>
   This functional component is all about rendering the Defective Items list/grid.
   Developer: Aashish Singh, Created Date: 20-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import noMessageIcon from '../../assets/Images/filterNodata.png';
import { ModuleType } from '../../constants/enums/module-type-enum';
import userSettingEnum from '../../constants/enums/user-setting-enum';
import { GridRemoteConstant } from '../../constants/grid-constant';
import { useStore } from '../../contexts/store-provider';
import { DefectiveItemFilterInitialState } from '../../core/initialState/filter-initial-state';
import {
  isOperationPermittedRoleBase,
  showIconsRoleBase,
} from '../../helpers/action-permission-helper';
import { getTenantIdFromLocalStorage } from '../../helpers/localstorage-helper';
import IPageInfo, {
  IActionFormatter,
  IPageLimitInfo,
} from '../../models/ICommon';
import { IDefectiveItemFilter } from '../../models/IDefectiveItemFilter';
import FormLoader from '../../shared-components/FormLoader/FormLoader';
import Grid from '../../shared-components/Grid/Grid';
import Pagination from '../../shared-components/Grid/Pagination';
import Header from '../../shared-components/header/header';
import {
  DeleteIcon,
  EditIcon,
  ImportHolidayIcon,
  PlusIcon,
  RefreshIcon,
} from '../../shared-components/svgs/svgs';
import { formatMessage } from '../../translations/format-message';
import AddDefectiveItem from './components/add-defective-item';
import ImportDefectiveItem from './components/import-defective-item';
import AddDefectiveItemPopupHOC from './hoc/add-defective-item-popup-hoc';
import ImportDefectiveItemPopupHOC from './hoc/import-defective-item-popup-hoc';
import FilterComponent from './components/filter';
import './defective-items.css';
import { CustomActionFormatter } from '../../shared-components/Grid/GridFormatter';
import DeletePopup from '../../shared-components/popup/delete/delete-popup';
import DeletePopupHOC from '../../shared-components/popup/delete/delete-popup-hoc';

/**
 * This function is used to render the Defective Items grid along with pagination, refresh and add actions.
 * @param props: Contains the add popup visibility flag and its toggle handler from the HOC
 */
const DefectiveItems = (props: any) => {
  const { defectiveItemsStore, preferencesStore } = useStore();
  const {
    GetDefectiveItemListService,
    allDefectiveItems,
    defectiveItemList,
    getAllDefectiveItemState,
    addDefectiveItemState,
    fileUploadState,
    resetFileUploadState,
    importResults,
    DeleteDefectiveItemService,
    deleteDefectiveItemState,
    resetDeleteDefectiveItemState,
    resetGetDefectiveItemByIdState,
  } = defectiveItemsStore;

  const {
    userSetting,
    setUserSetting,
    islanguageChanged,
    updateLanguageChangedFlag,
  } = preferencesStore;

  const allDefectiveItemData = allDefectiveItems;
  const isFirstLoad = useRef(true);
  const [disablePagination, setDisablePagination] = useState<boolean>(false);
  const pageLimitOptions: Array<number> = [10, 20, 50, 100];

  const [selectedRow, setSelectedRow] = useState<any>({});
  const [selectedId, setSelectedId] = useState(-1);
  const [openPanel, setOpenPanel] = useState(false);

  const [pageLimitInfo, setPageLimit] = useState<IPageLimitInfo>({
    pageLimit: userSetting.pageLimitConfig,
    isPageLimitChange: false,
  });

  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    currentPage: 1,
    totalPages: 1,
    isPagerChange: false,
    isRefreshPage: false,
  });

  const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);
  const [isFilterCleared, setIsFilterCleared] = useState(true);
  const [filterObj, setFilterObj] = useState<IDefectiveItemFilter>(
    DefectiveItemFilterInitialState
  );

  /**
   * This function is used to fetch the defective items list from the store for the current page and page size.
   * @param filter: Filter values to send - defaults to the current filterObj
   */
  const getDefectiveItemListServiceHandler = (filter?: IDefectiveItemFilter) => {
    GetDefectiveItemListService(
      Number(getTenantIdFromLocalStorage()),
      pageInfo.currentPage,
      pageLimitInfo.pageLimit,
      filter || filterObj
    );
  };

  /**
   * This function is used to apply the filter popup's submitted values.
   */
  const filterHandler = (object: IDefectiveItemFilter) => {
    setFilterObj(object);
    setPageInfo({ ...pageInfo, currentPage: 1, isPagerChange: false });
    GetDefectiveItemListService(
      Number(getTenantIdFromLocalStorage()),
      1,
      pageLimitInfo.pageLimit,
      object
    );
    setIsFilterCleared(false);
  };

  /**
   * This function is used to clear the filter.
   * @param obj: Reset filter values
   * @param isfilterCleard: Whether this call is clearing the filter
   */
  const filterParams = (obj: IDefectiveItemFilter, isfilterCleard: boolean) => {
    setFilterObj(obj);
    if (isfilterCleard) {
      setPageInfo({ ...pageInfo, currentPage: 1, isPagerChange: false });
      GetDefectiveItemListService(
        Number(getTenantIdFromLocalStorage()),
        1,
        pageLimitInfo.pageLimit,
        obj
      );
      setIsFilterCleared(true);
    }
  };

  /**
   * This useEffect is used to fetch the defective items list on the first render.
   */
  useEffect(() => {
    getDefectiveItemListServiceHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * This useEffect is used to refetch the defective items list whenever the language is changed.
   */
  useEffect(() => {
    if (islanguageChanged) {
      getDefectiveItemListServiceHandler();
      updateLanguageChangedFlag();
    }
  }, [islanguageChanged]);

  /**
   * This useEffect is used to sync pageInfo with the paging details returned from the API response.
   */
  useEffect(() => {
    setPageInfo({
      ...pageInfo,
      currentPage: defectiveItemList?.PagingDetails?.CurrentPage || 1,
      totalPages: defectiveItemList?.PagingDetails?.TotalPages,
      isPagerChange: false,
    });
    setDisablePagination(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defectiveItemList]);

  /**
   * This useEffect is used to reload the list whenever the current page changes.
   */
  useEffect(() => {
    if (pageInfo.isPagerChange) {
      getDefectiveItemListServiceHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageInfo.currentPage]);

  /**
   * This useEffect is used to reload the list whenever the page size limit changes.
   */
  useEffect(() => {
    if (pageLimitInfo.isPageLimitChange) {
      getDefectiveItemListServiceHandler();
      setPageLimit({ ...pageLimitInfo, isPageLimitChange: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLimitInfo.pageLimit]);

  /**
   * This useEffect is used to refresh the list after a new defective item has been added successfully.
   */
  useEffect(() => {
    if (addDefectiveItemState.success) {
      getDefectiveItemListServiceHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addDefectiveItemState.success]);

  /**
   * This useEffect is used to show a success toaster once the CSV import completes fully.
   */
  useEffect(() => {
    if (fileUploadState.success) {
      toast.success(formatMessage('ImportSuccess'));
      resetFileUploadState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUploadState.success]);

  useEffect(() => {
    if (importResults.length > 0 && importResults.some((result) => result.Success)) {
      getDefectiveItemListServiceHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importResults]);

  useEffect(() => {
    if (deleteDefectiveItemState.success) {
      toast.success(formatMessage('deleted_success'));
      props.deletePopupToggleHandler();
      resetDeleteDefectiveItemState();
      getDefectiveItemListServiceHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteDefectiveItemState.success]);

  /**
   * This function is used to update the current page and trigger a list reload.
   * @param pageNumber: Newly selected page number
   */
  const onPageChanged = (pageNumber: number) => {
    setPageInfo({ ...pageInfo, currentPage: pageNumber, isPagerChange: true });
  };

  /**
   * This function is used to update the page size limit, persist the user's preference and trigger a list reload.
   * @param evt: Change event from the page limit dropdown
   */
  const onPageLimitChanged = (evt: any) => {
    setPageInfo({ ...pageInfo, currentPage: 1, isPagerChange: false });
    setPageLimit({
      pageLimit: parseInt(evt.target.value),
      isPageLimitChange: true,
    });
    setUserSetting(userSettingEnum.UserLimitConfig, parseInt(evt.target.value));
  };

  /**
   * This function is used to reload the defective items list on refresh icon click.
   */
  const refreshClickHandler = () => {
    getDefectiveItemListServiceHandler();
  };

  const closePanelHandler = () => {
    setSelectedRow({});
    setOpenPanel(false);
  };

  /**
   * This function is used to open the Add Defective Item popup.
   * @param event: Mouse click event
   */
  const openAddDefectiveItemHandler = (event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedId(-1);
    resetGetDefectiveItemByIdState();
    props.addToggleHandler();
  };

  /**
   * This function is used to reset the selected item and close the Add/Edit Defective Item popup.
   */
  const closeAddDefectiveItemModalHandler = () => {
    setSelectedId(-1);
    resetGetDefectiveItemByIdState();
    props.addToggleHandler();
  };

  /**
   * This function is used to open the Import Defective Items CSV popup.
   * @param event: Mouse click event
   */
  const openImportDefectiveItemHandler = (event: React.MouseEvent) => {
    event.stopPropagation();
    props.importToggleHandler();
  };

  const openAddProcessHandler = (
    row: { Id: number },
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.stopPropagation();
    closePanelHandler();
    resetGetDefectiveItemByIdState();
    setSelectedId(row.Id);
    props.addToggleHandler();
  };

  const deleteClickHandler = (
    row: any,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    setSelectedId(row.Id);
    event.stopPropagation();
    closePanelHandler();
    resetGetDefectiveItemByIdState();
    props.deletePopupToggleHandler();
  };

  const deleteHandler = () => {
    DeleteDefectiveItemService(selectedId);
  };

  const actionIcons: IActionFormatter[] = showIconsRoleBase(
    [
      {
        dataField: 'isEdit',
        handler: openAddProcessHandler,
        icon: <EditIcon />,
        isDisable: !isOperationPermittedRoleBase(
          ModuleType.DefectiveItems,
          'Edit'
        ),
        // isDisable: true,
        title: 'edit',
        varient: 'primary',
        action: 'edit',
      },
      {
        dataField: 'isDelete',
        handler: deleteClickHandler,
        icon: <DeleteIcon />,
        isDisable: !isOperationPermittedRoleBase(
          ModuleType.DefectiveItems,
          'Delete'
        ),
        // isDisable: true,
        title: 'delete',
        varient: 'danger',
        action: 'delete',
      },
    ],
    ModuleType.DefectiveItems
  );

  const columns: any = [
    {
      dataField: 'serialNumber',
      text: formatMessage('serial_number'),
      classes: 'show-Elipses',
      isDummyField: true,
      formatter: (_: any, __: any, rowIndex: number) => rowIndex + 1,
      sort: false,
    },
    {
      dataField: 'Subject',
      text: formatMessage('subject'),
      classes: 'show-Elipses',
      sort: false,
    },
    {
      dataField: 'Item',
      text: formatMessage('item'),
      classes: 'show-Elipses',
      sort: false,
    },
    {
      dataField: 'Classification1',
      text: formatMessage('classification_1'),
      classes: 'show-Elipses',
      sort: false,
    },
    {
      dataField: 'Classification2',
      text: formatMessage('classification_2'),
      classes: 'show-Elipses',
      sort: false,
    },
    {
      dataField: 'Comments',
      text: formatMessage('comments'),
      classes: 'show-Elipses',
    },
    {
      dataField: '',
      formatter: CustomActionFormatter(actionIcons),
      text: formatMessage('actions'),
      classes: 'last-column',
      hidden: actionIcons.length > 0 ? false : true,
    },
  ];

  return (
    <>
      {props.showAddDefectiveItemPopup && (
        <AddDefectiveItem
          modalClosed={closeAddDefectiveItemModalHandler}
          id={selectedId}
        />
      )}
      {props.showDeletePopup && (
        <DeletePopup
          title="delete_item"
          modalSubmit={deleteHandler}
          modalClosed={props.deletePopupToggleHandler}
          message="message_delete_item_confirm"
          isLoading={deleteDefectiveItemState.inProgress}
        />
      )}
      {props.showImportPopup && (
        <ImportDefectiveItem id={-1} modalClosed={props.importToggleHandler} />
      )}
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-lg-6 col-md-6 col-sm-6">
            <div className="page_title_wrap">
              <h1 className="page_title">
                <FormattedMessage id="defective_items" />
              </h1>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <FormattedMessage id="home" />
                </li>
                <li
                  className="breadcrumb-item active"
                  aria-current="page"
                >
                  <FormattedMessage id="defective_items" />
                </li>
              </ol>
            </div>
          </div>
          <div className="col-lg-6 col-md-6 col-sm-6">
            <ul className="add-list">
              <li>
                <FilterComponent
                  initialValues={filterObj}
                  submitHandler={filterHandler}
                  filterParmas={filterParams}
                  isLoading={false}
                  isDisable={false}
                  isFilterCleared={isFilterCleared}
                  isFilterApplied={isFilterApplied}
                  setIsFilterApplied={setIsFilterApplied}
                />
              </li>
              <li title={formatMessage('refresh')}>
                <a
                  className="btn btn-light"
                  onClick={refreshClickHandler}
                >
                  <RefreshIcon />
                </a>
              </li>
              {isOperationPermittedRoleBase(ModuleType.DefectiveItems, 'Add') && (
                <li title={formatMessage('csv_import')}>
                  <a
                    className="btn btn-light"
                    onClick={openImportDefectiveItemHandler}
                  >
                    <ImportHolidayIcon />
                  </a>
                </li>
              )}
              {/* {isOperationPermittedRoleBase(ModuleType.DefectiveItems, 'Add') && ( */}
              {isOperationPermittedRoleBase(
                ModuleType.DefectiveItems,
                'Add'
              ) && (
                <li title={formatMessage('add_item')}>
                  <a
                    className="btn btn-secondary"
                    onClick={openAddDefectiveItemHandler}
                  >
                    <span className="me-1">
                      <PlusIcon />
                    </span>
                    <FormattedMessage id="add_item" />
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
      <div className="card card-page">
        {(getAllDefectiveItemState.inProgress ||
          addDefectiveItemState.inProgress) && (
          <FormLoader
            loading={
              getAllDefectiveItemState.inProgress ||
              addDefectiveItemState.inProgress
            }
          />
        )}
        {allDefectiveItemData && allDefectiveItemData.length > 0 ? (
          <>
            <div className="tableDesign row-eq-height tableDevices p-3 changingPanel">
              <div
                className="table-responsive"
                data-testid="table"
              >
                <Grid
                  data={allDefectiveItemData}
                  columns={columns}
                  keyField="Id"
                  loading={false}
                  hover={true}
                  remote={{ ...GridRemoteConstant }}
                />
              </div>
            </div>
            <div className="row no-gutters p-3 pt-0 tableFooter">
              <div className="col-lg-3 col-md-6 col-sm-6 pt-1">
                <FormattedMessage id="show" />
                <select
                  className="pageLimit"
                  onChange={onPageLimitChanged}
                  value={pageLimitInfo.pageLimit}
                >
                  {pageLimitOptions.map((op: any) => (
                    <option
                      key={op}
                      value={op}
                    >
                      {op}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-lg-9 col-md-6 col-sm-6 d-flex justify-content-sm-end">
                <Pagination
                  isRefreshPage={false}
                  totalPages={pageInfo.totalPages}
                  pageLimit={pageLimitInfo.pageLimit}
                  onPageChanged={onPageChanged}
                  disablePagination={disablePagination}
                  setIsRefreshPage={() =>
                    setPageInfo({ ...pageInfo, isRefreshPage: false })
                  }
                  currentPage={pageInfo.currentPage}
                  totalRecords={
                    defectiveItemList?.PagingDetails?.TotalRecords || 0
                  }
                />
              </div>
            </div>
          </>
        ) : (
          !getAllDefectiveItemState.inProgress && (
            <div className="message">
              <div className="d-flex message-area">
                <div className="no-data-img">
                  <img
                    src={noMessageIcon}
                    alt=""
                    className="no_message"
                  />
                </div>
                <div>
                  <h3 className="fw-bold display-6">
                    <FormattedMessage id="no_defective_items" />
                  </h3>
                  <h4 className="mb-3">
                    <FormattedMessage id="no_defective_items_message" />
                  </h4>
                  {isOperationPermittedRoleBase(
                    ModuleType.DefectiveItems,
                    'Add'
                  ) && (
                    <a
                      className="btn btn-primary"
                      onClick={openAddDefectiveItemHandler}
                    >
                      <span className="me-1">
                        <PlusIcon />
                      </span>
                      <FormattedMessage id="add_item" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          )
        )}
      </div>
    </>
  );
};

export default DeletePopupHOC(ImportDefectiveItemPopupHOC(
  AddDefectiveItemPopupHOC(observer(DefectiveItems)))
);
